'use client'

import React, { useState } from 'react'
import { BarChart, LineChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { ChevronDown } from 'lucide-react'

const companies = {
  companyA: '株式会社A',
  companyB: '株式会社B',
  companyC: '株式会社C',
}

const items = {
  stockPrice: '株価',
  revenue: '売上',
  operatingProfit: '営業利益',
  netIncome: '当期利益',
  per: 'PER',
}

const generateRandomData = () => {
  return Object.keys(items).reduce((acc, item) => {
    acc[item] = Array(12).fill(0).map((_, i) => ({
      month: `${i + 1}月`,
      value: Math.floor(Math.random() * 1000) + 100,
    }))
    return acc
  }, {})
}

const data = Object.keys(companies).reduce((acc, company) => {
  acc[company] = generateRandomData()
  return acc
}, {})

const CustomDropdown = ({ options, selected, onChange, label }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="relative">
      <button
        className="appearance-none bg-white border border-gray-300 rounded px-3 py-2 w-full text-left focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{selected.map(s => options[s]).join(', ')}</span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
          {Object.entries(options).map(([key, name]) => (
            <label key={key} className="block px-4 py-2 hover:bg-gray-100">
              <input
                type="checkbox"
                checked={selected.includes(key)}
                onChange={() => {
                  const newSelected = selected.includes(key)
                    ? selected.filter(s => s !== key)
                    : [...selected, key]
                  onChange(newSelected)
                }}
                className="mr-2"
              />
              {name}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

const Graph = () => {
  const [chartType, setChartType] = useState('line')
  const [selectedCompanies, setSelectedCompanies] = useState(['companyA'])
  const [selectedItems, setSelectedItems] = useState(['stockPrice'])

  const renderChart = () => {
    const chartData = selectedCompanies.flatMap(company => 
      selectedItems.map(item => ({
        name: `${companies[company]} - ${items[item]}`,
        data: data[company][item],
      }))
    )

    const ChartComponent = chartType === 'bar' ? BarChart : LineChart

    return (
      <ResponsiveContainer width="100%" height={400}>
        <ChartComponent data={chartData[0].data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <Tooltip />
          <Legend />
          {chartData.map(({ name, data }, index) => {
            const DataComponent = chartType === 'bar' ? Bar : Line
            return (
              <DataComponent
                key={name}
                type="linear"
                dataKey="value"
                data={data}
                name={name}
                stroke={`hsl(${index * 30}, 70%, 50%)`}
                fill={`hsl(${index * 30}, 70%, 50%)`}
              />
            )
          })}
        </ChartComponent>
      </ResponsiveContainer>
    )
  }

  return (
    <div className="p-4 w-full min-w-[400px]">
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-lg font-semibold">グラフ</h4>
        <div className="relative">
          <select
            className="appearance-none bg-white border border-gray-300 rounded px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
          >
            <option value="bar">棒グラフ</option>
            <option value="line">線グラフ</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>
      <div className="mb-4">
        <h5 className="font-semibold mb-2">表示項目</h5>
        <CustomDropdown
          options={items}
          selected={selectedItems}
          onChange={setSelectedItems}
          label="表示項目"
        />
      </div>
      <div className="mb-4">
        <h5 className="font-semibold mb-2">企業選択</h5>
        <CustomDropdown
          options={companies}
          selected={selectedCompanies}
          onChange={setSelectedCompanies}
          label="企業選択"
        />
      </div>
      {renderChart()}
    </div>
  )
}

export default Graph